import React, { useCallback, useMemo, useState, useRef } from "react"
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  Connection,
  addEdge,
  reconnectEdge,
  MarkerType,
  BackgroundVariant,
  OnNodesChange,
  OnEdgesChange,
  useReactFlow
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"

import { WorkflowNode, WorkflowEdge } from "lib/types/workflow"
import { nodeTypes } from "./nodes"
import AddNodeDialog, { NodeChoice } from "./AddNodeDialog"

import { CanvasContainer } from "./WorkflowCanvas.styled"

import {
  FIT_VIEW_PADDING,
  TerminalTarget,
  edgeTypes
} from "./constants"
import {
  findClosestEdge,
  findClosestTerminal
} from "./helpers/geometry"
import { makeEdge } from "./helpers/edges"
import { computeLayout } from "./helpers/layout"
import { createActionNode, createConditionNode } from "./helpers/nodeFactories"

type Props = {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
  onNodesChange: OnNodesChange<WorkflowNode>
  onEdgesChange: OnEdgesChange<WorkflowEdge>
  setNodes: React.Dispatch<React.SetStateAction<WorkflowNode[]>>
  setEdges: React.Dispatch<React.SetStateAction<WorkflowEdge[]>>
  onNodeClick?: (event: React.MouseEvent, node: WorkflowNode) => void
}

const WorkflowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setNodes,
  setEdges,
  onNodeClick
}: Props) => {
  const [dropTargetEdgeId, setDropTargetEdgeId] = useState<string | null>(null)
  const [dropTargetTerminal, setDropTargetTerminal] = useState<TerminalTarget | null>(
    null
  )
  const [animating, setAnimating] = useState(false)
  const [insertEdgeId, setInsertEdgeId] = useState<string | null>(null)
  const [insertTerminal, setInsertTerminal] = useState<{
    nodeId: string
    handleId: string
  } | null>(null)
  const draggedRef = useRef<string | null>(null)
  const { fitView } = useReactFlow()

  const appendNodeAtTerminal = useCallback(
    (parentId: string, sourceHandle: string, newNode: WorkflowNode) => {
      const edge = makeEdge(parentId, sourceHandle, newNode.id, "input")
      const nextNodes = [...nodes, newNode]
      const nextEdges = [...edges, edge]

      setNodes(nextNodes)
      setEdges(nextEdges)

      setAnimating(true)
      const layout = computeLayout(nextNodes, nextEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [nodes, edges, setNodes, setEdges, fitView]
  )

  const onAddAtTerminal = useCallback(
    (nodeId: string, handleId: string) => {
      setInsertTerminal({ nodeId, handleId })
    },
    []
  )

  const insertNodeOnEdge = useCallback(
    (edgeId: string, newNode: WorkflowNode) => {
      const targetEdge = edges.find((e) => e.id === edgeId)
      if (!targetEdge) return

      const outputHandle = newNode.type === "condition" ? "true-output" : "output"

      const newEdges = [
        ...edges.filter((e) => e.id !== edgeId),
        makeEdge(targetEdge.source, targetEdge.sourceHandle, newNode.id, "input"),
        makeEdge(newNode.id, outputHandle, targetEdge.target, targetEdge.targetHandle)
      ]

      const nextNodes = [...nodes, newNode]
      setNodes(nextNodes)
      setEdges(newEdges)

      setAnimating(true)
      const layout = computeLayout(nextNodes, newEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [edges, nodes, setEdges, setNodes, fitView]
  )

  const handleAddNodeSelect = useCallback(
    (choice: NodeChoice) => {
      const newNode = choice === "condition" ? createConditionNode() : createActionNode()
      if (insertEdgeId) {
        insertNodeOnEdge(insertEdgeId, newNode)
        setInsertEdgeId(null)
      } else if (insertTerminal) {
        appendNodeAtTerminal(insertTerminal.nodeId, insertTerminal.handleId, newNode)
        setInsertTerminal(null)
      }
    },
    [insertEdgeId, insertTerminal, insertNodeOnEdge, appendNodeAtTerminal]
  )

  const terminalHandlesByNode = useMemo(() => {
    const SOURCE_HANDLES: Record<string, string[]> = {
      trigger: ["output"],
      condition: ["true-output", "false-output"],
      action: ["output"]
    }
    const connected = new Set(
      edges.map((e) => `${e.source}::${e.sourceHandle ?? "output"}`)
    )
    const result = new Map<string, string[]>()
    for (const node of nodes) {
      const handles = SOURCE_HANDLES[node.type ?? ""] ?? []
      const terminals = handles.filter((h) => !connected.has(`${node.id}::${h}`))
      if (terminals.length > 0) result.set(node.id, terminals)
    }
    return result
  }, [nodes, edges])

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          _terminalHandles: terminalHandlesByNode.get(n.id) ?? [],
          _onAddAtTerminal: onAddAtTerminal,
          _activeTerminalDropHandle:
            dropTargetTerminal?.nodeId === n.id
              ? dropTargetTerminal.handleId
              : undefined
        }
      })),
    [nodes, terminalHandlesByNode, onAddAtTerminal, dropTargetTerminal]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      const edge = {
        ...connection,
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: true
      }
      setEdges((eds) => addEdge(edge, eds))
    },
    [setEdges]
  )

  const onReconnect = useCallback(
    (oldEdge: WorkflowEdge, newConnection: Connection) => {
      setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds))
    },
    [setEdges]
  )

  const isValidConnection = useCallback(
    (connection: WorkflowEdge | Connection) => {
      if (connection.source === connection.target) return false
      const existingEdge = edges.find(
        (e) =>
          e.source === connection.source &&
          e.sourceHandle === connection.sourceHandle
      )
      return !existingEdge
    },
    [edges]
  )

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      if (node.type === "trigger") return
      draggedRef.current = node.id
    },
    []
  )

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      if (!draggedRef.current) return
      const terminal = findClosestTerminal(
        node.position,
        draggedRef.current,
        nodes,
        terminalHandlesByNode
      )
      if (terminal) {
        setDropTargetTerminal(terminal)
        setDropTargetEdgeId(null)
        return
      }
      setDropTargetTerminal(null)
      const closest = findClosestEdge(node.position, draggedRef.current, nodes, edges)
      setDropTargetEdgeId(closest)
    },
    [nodes, edges, terminalHandlesByNode]
  )

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent) => {
      const draggedId = draggedRef.current
      const targetEdgeId = dropTargetEdgeId
      const targetTerminal = dropTargetTerminal

      draggedRef.current = null
      setDropTargetEdgeId(null)
      setDropTargetTerminal(null)

      if (!draggedId) return

      const draggedNode = nodes.find((n) => n.id === draggedId)
      if (!draggedNode) return

      if (targetTerminal) {
        if (targetTerminal.nodeId === draggedId) return

        const incoming = edges.filter((e) => e.target === draggedId)
        const outgoing = edges.filter((e) => e.source === draggedId)

        const edgesToRemove = new Set<string>()
        for (const e of incoming) edgesToRemove.add(e.id)
        for (const e of outgoing) edgesToRemove.add(e.id)

        const edgesToAdd: WorkflowEdge[] = []

        if (incoming.length === 1 && outgoing.length === 1) {
          edgesToAdd.push(
            makeEdge(
              incoming[0].source,
              incoming[0].sourceHandle,
              outgoing[0].target,
              outgoing[0].targetHandle
            )
          )
        }

        edgesToAdd.push(
          makeEdge(
            targetTerminal.nodeId,
            targetTerminal.handleId,
            draggedId,
            "input"
          )
        )

        const newEdges = [
          ...edges.filter((e) => !edgesToRemove.has(e.id)),
          ...edgesToAdd
        ]
        setEdges(newEdges)

        setAnimating(true)
        const layout = computeLayout(nodes, newEdges)
        setNodes((nds) =>
          nds.map((n) => {
            const pos = layout.get(n.id)
            return pos ? { ...n, position: pos } : n
          })
        )
        setTimeout(() => {
          fitView({ duration: 400, padding: FIT_VIEW_PADDING })
          setAnimating(false)
        }, 350)
        return
      }

      if (!targetEdgeId) return

      const targetEdge = edges.find((e) => e.id === targetEdgeId)
      if (!targetEdge) return

      const outputHandle = draggedNode.type === "condition" ? "true-output" : "output"

      const incoming = edges.filter((e) => e.target === draggedId)
      const outgoing = edges.filter((e) => e.source === draggedId)

      const edgesToRemove = new Set<string>([targetEdge.id])
      const edgesToAdd: WorkflowEdge[] = []

      for (const e of incoming) edgesToRemove.add(e.id)
      for (const e of outgoing) edgesToRemove.add(e.id)

      if (incoming.length === 1 && outgoing.length === 1) {
        edgesToAdd.push(
          makeEdge(
            incoming[0].source,
            incoming[0].sourceHandle,
            outgoing[0].target,
            outgoing[0].targetHandle
          )
        )
      }

      edgesToAdd.push(
        makeEdge(targetEdge.source, targetEdge.sourceHandle, draggedId, "input"),
        makeEdge(draggedId, outputHandle, targetEdge.target, targetEdge.targetHandle)
      )

      const newEdges = [
        ...edges.filter((e) => !edgesToRemove.has(e.id)),
        ...edgesToAdd
      ]
      setEdges(newEdges)

      setAnimating(true)
      const layout = computeLayout(nodes, newEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [
      dropTargetEdgeId,
      dropTargetTerminal,
      edges,
      nodes,
      setEdges,
      setNodes,
      fitView
    ]
  )

  const onAddClick = useCallback((edgeId: string) => {
    setInsertEdgeId(edgeId)
  }, [])

  const displayEdges = useMemo(() => {
    return edges.map((e) => {
      const branchLabel =
        e.sourceHandle === "true-output"
          ? "yes"
          : e.sourceHandle === "false-output"
            ? "no"
            : undefined
      const base: WorkflowEdge = {
        ...e,
        type: "addNode",
        data: { ...(e.data ?? {}), onAddClick, branchLabel }
      }
      if (e.id === dropTargetEdgeId) {
        return {
          ...base,
          className: "drop-target",
          label: "⊕ Drop to insert here",
          labelStyle: {
            fontSize: 12,
            fontWeight: 700,
            color: "#10b981",
            background: "#ecfdf5",
            padding: "4px 10px",
            borderRadius: "12px",
            border: "2px solid #10b981"
          },
          labelBgStyle: { display: "none" }
        }
      }
      return base
    })
  }, [edges, dropTargetEdgeId, onAddClick])

  const memoNodeTypes = useMemo(() => nodeTypes, [])
  const memoEdgeTypes = useMemo(() => edgeTypes, [])

  return (
    <CanvasContainer className={animating ? "animating" : ""}>
      <ReactFlow<WorkflowNode, WorkflowEdge>
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        onNodeClick={onNodeClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        isValidConnection={isValidConnection}
        edgesReconnectable
        nodeTypes={memoNodeTypes}
        edgeTypes={memoEdgeTypes}
        connectOnClick
        connectionRadius={40}
        fitView
        fitViewOptions={{ padding: FIT_VIEW_PADDING }}
        deleteKeyCode={["Backspace", "Delete"]}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <MiniMap />
      </ReactFlow>
      {(insertEdgeId || insertTerminal) && (
        <AddNodeDialog
          onSelect={handleAddNodeSelect}
          onClose={() => {
            setInsertEdgeId(null)
            setInsertTerminal(null)
          }}
        />
      )}
    </CanvasContainer>
  )
}

export default WorkflowCanvas
